import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAt,
  endAt
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { firestore, storage } from '../firebase/config';
import { BusinessPhotoUtils } from '../utils/geospatial';

/**
 * BusinessService - Centralized service for all business-related operations
 * This service handles business data fetching, processing, and image management
 * with automatic photo generation for businesses without images
 */
export class BusinessService {
  /**
   * Fetch businesses with geospatial filtering
   * @param {Object} bounds - Map bounds { ne: {lat, lng}, sw: {lat, lng} }
   * @param {number} maxResults - Maximum number of businesses to return
   * @returns {Array} Array of business objects with coordinate validation
   */
  static async fetchBusinessesInBounds(bounds, maxResults = 1000) {
    if (!bounds?.ne || !bounds?.sw) {
      throw new Error('Invalid bounds provided');
    }

    try {
      console.log(`🔍 BusinessService: Fetching businesses in bounds`);
      
      const { ne, sw } = bounds;
      const businessesRef = collection(firestore, 'businesses');
      
      let allBusinesses = [];
      let lastDoc = null;
      const batchSize = 500;
      
      while (true) {
        // Build query with geospatial constraints
        let currentQuery = query(
          businessesRef,
          where('address.coordinates.latitude', '>=', sw.lat),
          where('address.coordinates.latitude', '<=', ne.lat),
          orderBy('address.coordinates.latitude'),
          limit(batchSize)
        );
        
        // Add pagination if we have a last document
        if (lastDoc) {
          currentQuery = query(
            businessesRef,
            where('address.coordinates.latitude', '>=', sw.lat),
            where('address.coordinates.latitude', '<=', ne.lat),
            orderBy('address.coordinates.latitude'),
            startAt(lastDoc),
            limit(batchSize)
          );
        }
        
        const snapshot = await getDocs(currentQuery);
        
        if (snapshot.empty) {
          console.log(`📊 BusinessService: No more documents found. Total: ${allBusinesses.length}`);
          break;
        }
        
        // Filter by longitude and validate coordinates
        const batchBusinesses = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(business => {
            const lng = business.address?.coordinates?.longitude;
            const lat = business.address?.coordinates?.latitude;
            
            // Validate coordinates exist and are in bounds
            return lng && lat && 
                   typeof lng === 'number' && typeof lat === 'number' &&
                   lng >= sw.lng && lng <= ne.lng && 
                   lat >= sw.lat && lat <= ne.lat;
          });
        
        allBusinesses = allBusinesses.concat(batchBusinesses);
        
        console.log(`📄 BusinessService: Batch: ${snapshot.docs.length} docs, ${batchBusinesses.length} in bounds, total: ${allBusinesses.length}`);
        
        // Set up for next iteration
        lastDoc = snapshot.docs[snapshot.docs.length - 1];
        
        // Stop if we have enough results or fewer docs than requested
        if (allBusinesses.length >= maxResults || snapshot.docs.length < batchSize) {
          break;
        }
      }
      
      console.log(`✅ BusinessService: Found ${allBusinesses.length} businesses in bounds`);
      return allBusinesses.slice(0, maxResults);
      
    } catch (error) {
      console.error('❌ BusinessService: Error fetching businesses in bounds:', error);
      throw new Error(`Failed to fetch businesses in bounds: ${error.message}`);
    }
  }

  /**
   * Fetch multiple businesses by their IDs efficiently
   * @param {Array<string>} businessIds - Array of business IDs
   * @returns {Object} Map of businessId -> business data
   */
  static async fetchBusinessesBatch(businessIds) {
    if (!businessIds || businessIds.length === 0) {
      return {};
    }

    try {
      console.log(`🔍 BusinessService: Fetching ${businessIds.length} businesses`);
      
      const businessDataMap = {};
      
      // Fetch all businesses in parallel for efficiency
      const businessPromises = businessIds.map(async (businessId) => {
        try {
          const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            businessDataMap[businessId] = {
              ...businessData,
              id: businessId
            };
          } else {
            console.warn(`⚠️ Business ${businessId} not found`);
          }
        } catch (error) {
          console.error(`❌ Error fetching business ${businessId}:`, error);
        }
      });
      
      await Promise.all(businessPromises);
      
      console.log(`✅ BusinessService: Successfully fetched ${Object.keys(businessDataMap).length} businesses`);
      return businessDataMap;
      
    } catch (error) {
      console.error('❌ BusinessService: Error in batch fetch:', error);
      throw new Error(`Failed to fetch businesses: ${error.message}`);
    }
  }

  /**
   * Fetch a single business by ID
   * @param {string} businessId - Business ID
   * @returns {Object|null} Business data or null if not found
   */
  static async fetchBusinessById(businessId) {
    if (!businessId) {
      return null;
    }

    try {
      console.log(`🔍 BusinessService: Fetching business ${businessId}`);
      
      const businessDoc = await getDoc(doc(firestore, 'businesses', businessId));
      
      if (!businessDoc.exists()) {
        console.log('❌ BusinessService: Business not found');
        return null;
      }
      
      const businessData = { id: businessId, ...businessDoc.data() };
      
      console.log('✅ BusinessService: Business fetched successfully');
      return businessData;
      
    } catch (error) {
      console.error('❌ BusinessService: Error fetching business:', error);
      throw new Error(`Failed to fetch business: ${error.message}`);
    }
  }

  /**
   * Process business data and add enhanced image handling
   * @param {Array} businesses - Array of raw business objects
   * @returns {Array} Array of processed business objects with images
   */
  static async processBusinessesWithImages(businesses) {
    if (!businesses || businesses.length === 0) {
      return [];
    }

    try {
      console.log(`🎨 BusinessService: Processing ${businesses.length} businesses with images`);
      
      const processedBusinesses = await Promise.all(
        businesses.map(async (business) => {
          // Get business image with automatic generation fallback
          const imageUrl = await this.getBusinessImageUrl(
            business.businessImage?.path, 
            business.name
          );
          
          return {
            ...business,
            imageUrl,
            // Add processed address for consistent display
            formattedAddress: this.formatBusinessAddress(business),
            // Add coordinate validation flag
            hasValidCoordinates: this.validateCoordinates(business)
          };
        })
      );
      
      console.log(`✅ BusinessService: Processed ${processedBusinesses.length} businesses with images`);
      return processedBusinesses;
      
    } catch (error) {
      console.error('❌ BusinessService: Error processing businesses with images:', error);
      throw new Error(`Failed to process businesses: ${error.message}`);
    }
  }

  /**
   * Get business image URL with automatic fallback generation
   * @param {string} businessImagePath - Firebase storage path
   * @param {string} businessName - Business name for auto-generation
   * @returns {string} Image URL or generated data URL
   */
  static async getBusinessImageUrl(businessImagePath, businessName) {
    // Generate automatic image if no path provided
    if (!businessImagePath) {
      return BusinessPhotoUtils.generateBusinessPhotoDataUrl(businessName);
    }
    
    try {
      const imageRef = ref(storage, businessImagePath);
      const url = await getDownloadURL(imageRef);
      return url;
    } catch (error) {
      // Silently handle missing images and generate automatic fallback
      if (error.code !== 'storage/object-not-found') {
        console.error("Error getting business image:", error);
      }
      // Generate automatic image as fallback
      return BusinessPhotoUtils.generateBusinessPhotoDataUrl(businessName);
    }
  }

  /**
   * Format business address for consistent display
   * @param {Object} business - Business object
   * @returns {string} Formatted address string
   */
  static formatBusinessAddress(business) {
    const address = business.address;
    
    if (!address) {
      return 'Address not specified';
    }
    
    if (address.street) {
      return `${address.street}, ${address.city || ''}, ${address.state || ''} ${address.zipcode || ''}`.trim();
    }
    
    if (address.city) {
      return `${address.city}, ${address.state || ''} ${address.zipcode || ''}`.trim();
    }
    
    return 'Address not specified';
  }

  /**
   * Validate business coordinates
   * @param {Object} business - Business object
   * @returns {boolean} True if coordinates are valid
   */
  static validateCoordinates(business) {
    const coords = business.address?.coordinates;
    
    if (!coords) return false;
    
    const { latitude, longitude } = coords;
    
    return typeof latitude === 'number' && 
           typeof longitude === 'number' &&
           latitude >= -90 && latitude <= 90 &&
           longitude >= -180 && longitude <= 180;
  }

  /**
   * Search businesses by name or location
   * @param {string} searchQuery - Search term
   * @param {number} limit - Maximum results to return
   * @returns {Array} Array of matching businesses
   */
  static async searchBusinesses(searchQuery, limit = 20) {
    if (!searchQuery || searchQuery.trim().length < 2) {
      return [];
    }

    try {
      console.log(`🔍 BusinessService: Searching businesses for "${searchQuery}"`);
      
      const businessesRef = collection(firestore, 'businesses');
      
      // Get all businesses (could be optimized with text search in production)
      const allBusinessesQuery = query(businessesRef, limit(200));
      const snapshot = await getDocs(allBusinessesQuery);
      
      const searchTerm = searchQuery.toLowerCase().trim();
      
      // Client-side filtering for flexible search
      const matchingBusinesses = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(business => {
          const nameMatch = business.name?.toLowerCase().includes(searchTerm);
          const cityMatch = business.address?.city?.toLowerCase().includes(searchTerm);
          const typeMatch = business.type?.toLowerCase().includes(searchTerm);
          
          return nameMatch || cityMatch || typeMatch;
        })
        .slice(0, limit);
      
      console.log(`✅ BusinessService: Found ${matchingBusinesses.length} matching businesses`);
      return matchingBusinesses;
      
    } catch (error) {
      console.error('❌ BusinessService: Error searching businesses:', error);
      throw new Error(`Failed to search businesses: ${error.message}`);
    }
  }

  /**
   * Get businesses by type/category
   * @param {string} businessType - Type of business
   * @param {number} limit - Maximum results
   * @returns {Array} Array of businesses of specified type
   */
  static async getBusinessesByType(businessType, limit = 50) {
    try {
      console.log(`🔍 BusinessService: Fetching businesses of type "${businessType}"`);
      
      const businessesRef = collection(firestore, 'businesses');
      const typeQuery = query(
        businessesRef,
        where('type', '==', businessType),
        orderBy('name'),
        limit(limit)
      );
      
      const snapshot = await getDocs(typeQuery);
      
      const businesses = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log(`✅ BusinessService: Found ${businesses.length} businesses of type "${businessType}"`);
      return businesses;
      
    } catch (error) {
      console.error('❌ BusinessService: Error fetching businesses by type:', error);
      throw new Error(`Failed to fetch businesses by type: ${error.message}`);
    }
  }

  /**
   * Get nearby businesses using geohash approximation
   * @param {Object} center - Center point {lat, lng}
   * @param {number} radiusKm - Search radius in kilometers
   * @param {number} limit - Maximum results
   * @returns {Array} Array of nearby businesses with distance
   */
  static async getNearbyBusinesses(center, radiusKm = 10, limit = 50) {
    try {
      console.log(`🔍 BusinessService: Finding businesses within ${radiusKm}km of (${center.lat}, ${center.lng})`);
      
      // Calculate bounding box for initial filtering
      const latDelta = radiusKm / 111; // Rough conversion: 1 degree ≈ 111km
      const lngDelta = radiusKm / (111 * Math.cos(center.lat * Math.PI / 180));
      
      const bounds = {
        sw: { lat: center.lat - latDelta, lng: center.lng - lngDelta },
        ne: { lat: center.lat + latDelta, lng: center.lng + lngDelta }
      };
      
      // Fetch businesses in bounding box
      const businessesInBounds = await this.fetchBusinessesInBounds(bounds, limit * 2);
      
      // Calculate actual distances and filter by radius
      const nearbyBusinesses = businessesInBounds
        .map(business => {
          const coords = business.address?.coordinates;
          if (!coords) return null;
          
          const distance = this.calculateDistance(
            center.lat, center.lng,
            coords.latitude, coords.longitude
          );
          
          return {
            ...business,
            distance: Math.round(distance * 100) / 100 // Round to 2 decimal places
          };
        })
        .filter(business => business && business.distance <= radiusKm)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, limit);
      
      console.log(`✅ BusinessService: Found ${nearbyBusinesses.length} businesses within ${radiusKm}km`);
      return nearbyBusinesses;
      
    } catch (error) {
      console.error('❌ BusinessService: Error finding nearby businesses:', error);
      throw new Error(`Failed to find nearby businesses: ${error.message}`);
    }
  }

  /**
   * Calculate distance between two points using Haversine formula
   * @param {number} lat1 - First point latitude
   * @param {number} lng1 - First point longitude
   * @param {number} lat2 - Second point latitude
   * @param {number} lng2 - Second point longitude
   * @returns {number} Distance in kilometers
   */
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   * @param {number} value - Value in degrees
   * @returns {number} Value in radians
   */
  static toRad(value) {
    return value * Math.PI / 180;
  }
}

export default BusinessService; 